// Voice processing in getUserMedia is a mono pipeline. Leaving it on sums a
// stereo line (Spotify via a loopback or interface) to one channel.
export const stereoInputConstraints = (
  deviceId: string
): MediaTrackConstraints => ({
  deviceId: deviceId ? { exact: deviceId } : undefined,
  channelCount: { ideal: 2 },
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
})

type InputTrack = {
  getSettings?: () => { channelCount?: number }
  getCapabilities?: () => { channelCount?: { max?: number } }
  applyConstraints?: (constraints: MediaTrackConstraints) => Promise<void>
}

const readChannelCount = (track: InputTrack | undefined): number => {
  const count = track?.getSettings?.().channelCount
  return typeof count === 'number' && count >= 1 ? count : 1
}

// Sync peek so the first frame can start before applyConstraints.
export const peekChannelCount = (track: InputTrack | undefined): number =>
  readChannelCount(track)

// getUserMedia can still open one channel. Ask again when the device can do two.
export const deliveredStereoChannels = async (
  track: InputTrack | undefined
): Promise<number> => {
  if (readChannelCount(track) >= 2) return readChannelCount(track)

  const max = track?.getCapabilities?.().channelCount?.max ?? 1
  if (max < 2 || !track?.applyConstraints) return readChannelCount(track)

  try {
    await track.applyConstraints({
      channelCount: { ideal: 2 },
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    })
  } catch {
    // The device stayed mono.
  }

  return readChannelCount(track)
}
